import { useState } from 'react';
import { ZodError } from 'zod';

import {
  ApiBuyTokenUnauthorizedForTradeError,
  ApiError,
  ApiSellAmountTooSmallError,
  ApiSellTokenUnauthorizedForTradeError,
} from '@/lib/errors/api';
import {
  JupiterApiError,
  JupiterFetchRoutesError,
  JupiterRoutePlanDoesNotConsumeAllTheAmountError,
  JupiterTokenNotTradableError,
} from '@/lib/errors/jupiter';

import { useErrorReporting } from '@/hooks/useErrorReporting';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';

import { InlineAlertBox } from '../../Alerts/InlineAlertBox';
import { DetailsModal } from '../DetailsModal';

interface InlineErrorProps {
  /** Error to display */
  error?: ApiError | JupiterApiError | ZodError | Error;
  /** Whether the error is due to no liquidity */
  noLiquidity?: boolean;
}

interface ErrorDescription {
  /** Title of the error */
  title: string;
  /** Message of the error */
  description: string;
  level?: 'error' | 'info';
}

/**
 * Map of error names to the error description to render
 */
const errorToDescription: Record<string, ErrorDescription> = {
  ApiInputInvalidError: {
    title: 'Invalid input',
    description: 'Please ensure the details of your trade are correct.',
  },
  ApiInsufficientBalanceError: {
    title: 'Insufficient balance',
    description: 'You do not have enough balance to complete this transaction.',
  },
  ApiSwapValidationFailedError: {
    title: 'Swap validation failed',
    description: 'Please ensure the details of your trade are correct.',
  },
  ApiTokenNotSupportedError: {
    title: 'Unsupported',
    description: 'Token is not supported for trading.',
    level: 'info',
  },
  ApiTakerNotAuthorizedForTrade: {
    title: 'Unauthorized',
    description: 'The taker is not authorized from trading.',
  },
  ApiMetaTransactionExpiryTooSoonError: {
    title: 'Quote expired',
    description: 'This price is no longer valid. We are getting a new one.',
  },
  RECIPIENT_NOT_SUPPORTED: {
    title: 'Place order & transfer not supported',
    description:
      'This pair doesn’t support changing the receiving wallet. Use the connected wallet instead.',
    level: 'info',
  },
  NoLiquidityError: {
    title: 'No liquidity',
    description: 'There is not enough liquidity for this trade.',
  },
  Uncategorized: {
    title: 'Something went wrong',
    description: 'Unable to get a price for the trade.',
  },
};

/**
 * Inline error displayed in the transaction module for the price step.
 */
export const InlineError = ({
  error,
  noLiquidity,
}: InlineErrorProps): React.ReactElement | null => {
  useErrorReporting(
    error instanceof ApiError ? 'txn_module_zeroex_api_error' : 'txn_module_matcha_error',
    error,
  );
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const { recipientAddress, setRecipientAddress } =
    useMarketTradeSettingsStore(recipientAddressSelector);

  // no liquidity error is handled separately because it's not returned as an
  // error from the API, but rather as part of a successful response (code 200)
  if (noLiquidity) {
    return (
      <div className="mx-6 mt-6">
        <InlineAlertBox
          title={errorToDescription['NoLiquidityError'].title}
          description={errorToDescription['NoLiquidityError'].description}
        />
      </div>
    );
  }

  if (
    !error ||
    // the following errors are handled by `useSwapValidationError` hook
    error instanceof ApiBuyTokenUnauthorizedForTradeError ||
    error instanceof ApiSellAmountTooSmallError ||
    error instanceof ApiSellTokenUnauthorizedForTradeError ||
    error instanceof JupiterTokenNotTradableError ||
    error instanceof JupiterFetchRoutesError ||
    error instanceof JupiterRoutePlanDoesNotConsumeAllTheAmountError
  )
    return null;

  const inlineErr =
    error instanceof ApiError || error instanceof JupiterApiError
      ? (() => {
          if (error instanceof ApiError) {
            const detailsKey = error.details?.name;
            if (detailsKey && errorToDescription[detailsKey]) {
              return errorToDescription[detailsKey];
            }
          }
          return errorToDescription[error.name] ?? errorToDescription['Uncategorized'];
        })()
      : errorToDescription['Uncategorized'];
  const isRecipientNotSupported =
    error instanceof ApiError && error.details?.name === 'RECIPIENT_NOT_SUPPORTED';
  const shouldShowRemoveRecipient = isRecipientNotSupported && !!recipientAddress;
  const alertLevel = inlineErr.level ?? 'error';
  const description = (
    <p>
      <span className="inline-block mr-1">{inlineErr.description}</span>
      <button
        className={classNames(
          fontStyles({ variant: 'BODY_MD' }),
          alertLevel === 'info' ? 'text-token-gray-1200' : 'text-token-red-1300',
          'inline-block font-medium underline',
        )}
        onClick={() => {
          if (shouldShowRemoveRecipient) {
            setRecipientAddress(undefined);
            return;
          }
          setShowDetails(true);
        }}
      >
        {shouldShowRemoveRecipient ? 'Use connected wallet' : 'More details'}
      </button>
    </p>
  );

  return (
    <div className="mx-6 mt-6">
      <InlineAlertBox level={alertLevel} title={inlineErr.title} description={description} />
      <DetailsModal error={error} isOpen={showDetails} onOpenChange={setShowDetails} />
    </div>
  );
};
